import React, { useState, useEffect } from 'react';
import './App.css';
import { AUTH_CONFIG } from './config';
import { motion, useReducedMotion } from 'framer-motion';
import CIOReport from './CIOReport';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  }
};

const slideInRight = {
  hidden: { opacity: 0, x: 32 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  }
};

const BrandLogo = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" aria-label="Mangum Street logo" role="img">
    <defs>
      <linearGradient id="msg" x1="0" x2="1" y1="0" y2="1">
        <stop offset="0%" stopColor="#3B82F6" />
        <stop offset="100%" stopColor="#93C5FD" />
      </linearGradient>
    </defs>
    <rect x="8" y="8" width="84" height="84" rx="18" fill="url(#msg)" opacity="0.18" />
    <rect x="20" y="24" width="60" height="52" rx="10" fill="rgba(59,130,246,0.1)" stroke="#3B82F6" strokeWidth="2.5" />
    <circle cx="38" cy="47" r="9" fill="#3B82F6" opacity="0.3" />
    <path d="M38 47 L46 47 A9 9 0 0 0 38 38 Z" fill="#3B82F6" />
    <rect x="54" y="42" width="6" height="14" rx="2" fill="#3B82F6" opacity="0.85" />
    <rect x="62" y="38" width="6" height="18" rx="2" fill="#3B82F6" opacity="0.6" />
    <rect x="70" y="46" width="6" height="10" rx="2" fill="#3B82F6" opacity="0.4" />
    <path d="M26 62 C34 56, 42 66, 50 58 S66 64, 74 60" stroke="#3B82F6" strokeWidth="2.5" fill="none" opacity="0.75" />
  </svg>
);

const AppGlyph = ({ id }) => {
  // 28x28 glyph, white strokes for contrast
  switch (id) {
    case 'dashboard': // Investor Portfolio
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 22V6a2 2 0 0 1 2-2h14" opacity="0.5" />
            <path d="M9 22v-6M14 22v-9M19 22v-4" />
            <path d="M9 8a5 5 0 1 0 5-5" opacity="0.6" />
          </g>
        </svg>
      );
    case 'warroom':
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 8l10-4 10 4v6c0 6-4.5 10-10 12C8.5 24 4 20 4 14V8z" />
            <path d="M8 15c2-2 4 2 6 0s4-4 6-2" opacity="0.7" />
          </g>
        </svg>
      );
    case 'cycle-dashboard':
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 3a11 11 0 1 1-7.8 3.2" opacity="0.6" />
            <path d="M6 6h5V1" />
          </g>
        </svg>
      );
    case 'black-swan':
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 18c2-4 5-6 9-6 4 0 7 3 11 2" />
            <path d="M16 7c0 2-2 3-3 3 0-2 1-4 3-4" opacity="0.7" />
          </g>
        </svg>
      );
    case 'portfolio-simulator':
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="8" width="16" height="12" rx="2" />
            <path d="M8 8V6a2 2 0 0 1 2-2h8v4" />
            <path d="M6 18l4-4 3 3 5-5" />
          </g>
        </svg>
      );
    case 'modern-portfolio-theory':
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="14" cy="14" r="10" opacity="0.5" />
            <path d="M14 4v20M4 14h20" opacity="0.3" />
            <path d="M8 20c0-8 12-8 12 0" />
            <circle cx="10" cy="18" r="2" fill="#ffffff" />
            <circle cx="18" cy="18" r="2" fill="#ffffff" />
          </g>
        </svg>
      );
    case 'fundamental-dashboard':
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 6h14M7 12h10M7 18h6" />
            <rect x="5" y="4" width="18" height="20" rx="3" opacity="0.4" />
          </g>
        </svg>
      );
    case 'secret-sauce':
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 4l2 4 4.5.5-3.5 3 1 4.5-4-2-4 2 1-4.5-3.5-3 4.5-.5z" />
            <circle cx="14" cy="20" r="4" opacity="0.6" />
          </g>
        </svg>
      );
    // Trading/Data/Components glyphs
    case 'mcp-trading':
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 18l6-6 4 4 6-6" />
            <path d="M4 8h6v6H4zM18 18h6v6h-6z" opacity="0.5" />
          </g>
        </svg>
      );
    case 'trading':
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 20h16M10 20V8M18 20V4" />
            <path d="M10 8l3-3 3 3" />
          </g>
        </svg>
      );
    case 'strategy-tracker':
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="14" cy="14" r="10" opacity="0.5" />
            <path d="M8 16l4-4 3 3 5-5" />
          </g>
        </svg>
      );
    case 'bitcoin-strategy-sim':
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 6h6a4 4 0 0 1 0 8h-6zM11 14h7a4 4 0 0 1 0 8h-7z" opacity="0.8" />
          </g>
        </svg>
      );
    case 'ap-sync':
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 11a7 7 0 0 1 12-3" />
            <path d="M7 11H4l3-3" />
            <path d="M21 17a7 7 0 0 1-12 3" />
            <path d="M21 17h3l-3 3" />
          </g>
        </svg>
      );
    case 'analytics':
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 20h16" />
            <path d="M9 20v-6M14 20v-9M19 20v-4" />
          </g>
        </svg>
      );
    case 'news':
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="6" width="18" height="16" rx="3" />
            <path d="M9 10h10M9 14h8M9 18h6" />
          </g>
        </svg>
      );
    case 'micro':
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="10" cy="12" r="3" />
            <circle cx="18" cy="16" r="3" />
            <path d="M12 14l4 2" />
          </g>
        </svg>
      );
    case 'market':
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 16c4-8 12-8 16 0" />
            <path d="M10 16v4M18 16v4" opacity="0.6" />
          </g>
        </svg>
      );
    case 'position':
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 4v20M4 14h20" opacity="0.5" />
            <rect x="8" y="8" width="12" height="12" rx="2" />
          </g>
        </svg>
      );
    case 'crypto-phase':
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="14" cy="14" r="10" />
            <path d="M14 4a10 10 0 0 1 0 20" fill="#ffffff" fillOpacity="0.3" />
            <path d="M10 10h8M10 14h5M10 18h3" opacity="0.7" />
          </g>
        </svg>
      );
    case 'cio-report':
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="4" width="18" height="20" rx="2" />
            <path d="M9 9h10M9 13h10M9 17h6" />
            <circle cx="19" cy="19" r="5" fill="#dc2626" opacity="0.3" />
            <path d="M19 17v2h2" stroke="#dc2626" strokeWidth="1.5" />
          </g>
        </svg>
      );
    case 'cascade-engine':
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 4l-8 6v4l8 6 8-6v-4z" />
            <path d="M6 14l8 6 8-6" opacity="0.6" />
            <path d="M6 18l8 6 8-6" opacity="0.3" />
            <circle cx="14" cy="10" r="2" fill="#ffffff" />
          </g>
        </svg>
      );
    case 'option-volatility-surface':
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 20l6-8 4 4 6-8 4 6" />
            <path d="M4 16c2-4 6-6 10-6s8 2 10 6" opacity="0.5" />
            <path d="M4 12c3-6 7-8 10-8s7 2 10 8" opacity="0.3" />
            <circle cx="10" cy="12" r="1.5" fill="#ffffff" />
            <circle cx="18" cy="8" r="1.5" fill="#ffffff" />
          </g>
        </svg>
      );
    case 'hedge-menu':
      return (
        <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
          <g fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 14l10-10 10 10" />
            <path d="M4 14l10 10 10-10" opacity="0.5" />
            <rect x="12" y="8" width="4" height="12" rx="1" fill="#ffffff" opacity="0.8" />
            <circle cx="14" cy="14" r="8" opacity="0.3" />
          </g>
        </svg>
      );
    default:
      return null;
  }
};

const getAbbreviation = (name) => {
  const words = name.split(/\s|&|-|\//).filter(Boolean);
  const initials = words.map(w => w[0]).join('').toUpperCase();
  return initials.slice(0, 3);
};

// Simple Login Component
const Login = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const prefersReducedMotion = useReducedMotion();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === AUTH_CONFIG.password) {
      localStorage.setItem(AUTH_CONFIG.session.storageKey, 'true');
      onLogin();
    } else {
      setError('Invalid password');
    }
  };

  return (
    <div className="login-container">
      <motion.div
        className="login-box"
        initial={prefersReducedMotion ? undefined : { opacity: 0, y: 20 }}
        animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="brand-inline"><BrandLogo size={36} /><span className="brand-name">Mangum Street</span></div>
        <p className="login-tagline">Navigation Portal</p>
        <form onSubmit={handleSubmit} className="login-form">
          <input
            type="password"
            placeholder="Enter password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="login-input"
            autoFocus
          />
          <motion.button type="submit" className="login-button" whileTap={prefersReducedMotion ? undefined : { scale: 0.98 }}>
            Access Platform
          </motion.button>
          {error && <p className="login-error">{error}</p>}
        </form>
      </motion.div>
    </div>
  );
};

// Trading Tools Authentication Modal
const TradingAuthModal = ({ isOpen, onAuthenticate, onClose }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    // Check trading tools password
    if (password === AUTH_CONFIG.tradingPassword) {
      localStorage.setItem(AUTH_CONFIG.tradingSession.storageKey, 'true');
      // Set expiration time (optional - expires after configured hours)
      const expirationTime = new Date().getTime() + (AUTH_CONFIG.tradingSession.duration * 60 * 60 * 1000);
      localStorage.setItem(AUTH_CONFIG.tradingSession.expirationKey, expirationTime.toString());
      onAuthenticate();
      setPassword('');
      setError('');
    } else {
      setError('Invalid trading password');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-box">
        <button className="modal-close" onClick={onClose}>×</button>
        <h2 className="modal-title">Trading Tools Access</h2>
        <p className="modal-description">
          Additional authentication required for trading tools
        </p>
        <form onSubmit={handleSubmit} className="modal-form">
          <input
            type="password"
            placeholder="Enter trading password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="modal-input"
            autoFocus
          />
          <div className="modal-buttons">
            <button type="button" onClick={onClose} className="modal-cancel">
              Cancel
            </button>
            <button type="submit" className="modal-submit">
              Authenticate
            </button>
          </div>
          {error && <p className="modal-error">{error}</p>}
        </form>
      </div>
    </div>
  );
};

// Main Dashboard Component
const Dashboard = ({ onLogout }) => {
  const [isTradingAuthenticated, setIsTradingAuthenticated] = useState(false);
  const [showTradingAuthModal, setShowTradingAuthModal] = useState(false);
  const [pendingTradingUrl, setPendingTradingUrl] = useState(null);
  const [showCIOReport, setShowCIOReport] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  // Load ElevenLabs bot only when on main dashboard
  useEffect(() => {
    if (!showCIOReport) {
      // Load ElevenLabs script
      const script = document.createElement('script');
      script.src = 'https://elevenlabs.io/convai-widget/index.js';
      script.async = true;
      script.type = 'text/javascript';
      document.body.appendChild(script);

      // Create ElevenLabs element
      const elevenLabsElement = document.createElement('elevenlabs-convai');
      elevenLabsElement.setAttribute('agent-id', 'agent_01jzjkw0speqta1g4tb995sbxs');
      document.body.appendChild(elevenLabsElement);

      // Cleanup function
      return () => {
        // Remove script and element when component unmounts or showCIOReport changes
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
        if (elevenLabsElement.parentNode) {
          elevenLabsElement.parentNode.removeChild(elevenLabsElement);
        }
      };
    }
  }, [showCIOReport]);

  useEffect(() => {
    const checkTradingAuth = () => {
      const tradingAuth = localStorage.getItem(AUTH_CONFIG.tradingSession.storageKey);
      const expiration = localStorage.getItem(AUTH_CONFIG.tradingSession.expirationKey);
      if (tradingAuth === 'true' && expiration) {
        const now = new Date().getTime();
        if (now < parseInt(expiration)) {
          setIsTradingAuthenticated(true);
        } else {
          localStorage.removeItem(AUTH_CONFIG.tradingSession.storageKey);
          localStorage.removeItem(AUTH_CONFIG.tradingSession.expirationKey);
          setIsTradingAuthenticated(false);
        }
      }
    };
    checkTradingAuth();
    const interval = setInterval(checkTradingAuth, 60000);
    return () => clearInterval(interval);
  }, []);

  const mainApps = [
    { id: 'fundamental-dashboard', name: 'Fundamental Dashboard', description: 'Analytics for Developers', url: 'https://fundamental-dashboard.vercel.app/', color: '#06b6d4', isExternal: true },
    { id: 'warroom', name: 'War Room', description: 'Strategic command center with Analytics, News, Microstructure, Market Health, and Risk tools', url: 'https://warroom.mangumstreet.com', color: '#ef4444' },
    { id: 'dashboard', name: 'Investor Portfolio', description: 'Central operations and portfolio overview', url: 'https://investor-portfolio-phi.vercel.app/', color: '#3b82f6' },
    { id: 'cycle-dashboard', name: 'Cycle Dashboard', description: 'Market cycle analysis (Public Access)', url: 'https://cycle-dashboard-field2.vercel.app/', color: '#10b981', isPublic: true },
    { id: 'black-swan', name: 'Black Swan', description: 'Extreme market event analysis and risk monitoring', url: 'https://blackswan-dashboard.onrender.com', color: '#f59e0b', isExternal: true },
    { id: 'portfolio-simulator', name: 'Portfolio Simulator', description: 'Compare portfolio performance with and without crypto allocation', url: 'https://mpt-henna.vercel.app/', color: '#8b5cf6', isExternal: true },
    { id: 'modern-portfolio-theory', name: 'Modern Portfolio Theory', description: 'Optimize portfolio allocation using MPT principles', url: 'https://mpt-rerh.vercel.app/', color: '#ec4899', isExternal: true },
    { id: 'secret-sauce', name: 'Secret Sauce Rankings', description: 'FE score-based crypto rankings across 50 parameters', url: 'https://secret-sauce-frontend.vercel.app/', color: '#22c55e', isExternal: true },
    { id: 'crypto-phase', name: 'Crypto Phase', description: 'Real-time crypto market phase identification based on multiple data points and beliefs', url: 'https://crypto-phase.vercel.app/', color: '#00D4FF', isExternal: true },
    { id: 'cio-report', name: 'CIO Report', description: 'Executive-level insights and strategic technology reports with AI assistant', url: 'https://cio-report.vercel.app/', color: '#dc2626', isExternal: true },
    { id: 'cascade-engine', name: 'Cascade Engine', description: 'Advanced cascade analysis engine', url: 'https://cascade-engine-gaon.onrender.com', color: '#14b8a6', isExternal: true },
    { id: 'option-volatility-surface', name: 'Option Volatility Surface', description: '3D visualization of option implied volatility across strikes and expirations', url: 'https://option-volatility-surface.vercel.app/', color: '#9333ea', isExternal: true },
    { id: 'hedge-menu', name: 'Hedge Menu', description: 'Real-time position hedging strategies and risk management tools', url: 'https://hedge-menu-git-main-field2.vercel.app/', color: '#0891b2', isExternal: true },
    { id: 'spread-tracker', name: 'Spread Tracker', description: 'Track and analyze spreads', url: 'https://spread-tracker.vercel.app/', color: '#059669', isExternal: true },
    { id: 'trading-ledger', name: 'Trading Ledger', description: 'Track and manage trading transactions', url: 'https://trading-ledger.vercel.app/', color: '#6366f1', isExternal: true },
    { id: 'field-elevate-crm', name: 'Field Elevate CRM', description: 'Customer relationship management platform (work in progress)', url: 'https://field-elevate-crm.vercel.app/', color: '#d946ef', isExternal: true }
  ];
 
  // Fixed order across a 4-column grid: 4 on top, 4 on second row, 5 on third
  const primaryOrder = [
    'black-swan',
    'fundamental-dashboard',
    'warroom',
    'portfolio-simulator',
    'dashboard',
    'cycle-dashboard',
    'modern-portfolio-theory',
    'secret-sauce',
    'crypto-phase',
    'cio-report',
    'cascade-engine',
    'option-volatility-surface',
    'hedge-menu',
    'spread-tracker',
    'trading-ledger',
    'field-elevate-crm'
  ];
  const primaryAppsOrdered = primaryOrder
    .map(id => mainApps.find(a => a.id === id))
    .filter(Boolean);

  const tradingApps = [
    {
      id: 'mcp-trading',
      name: 'MCP Trading Dashboard',
      description: 'Multi-currency pair trading interface and analytics',
      url: 'https://mcp.mangumstreet.com',
      color: '#0ea5e9'
    },
    {
      id: 'trading',
      name: 'Trading',
      description: 'Live trading execution',
      url: 'https://trading.mangumstreet.com',
      color: '#22c55e'
    },
    {
      id: 'strategy-tracker',
      name: 'Strategy Tracker',
      description: 'Strategy performance monitoring',
      url: 'https://strategy-tracker.mangumstreet.com',
      color: '#a855f7'
    },
    {
      id: 'bitcoin-strategy-sim',
      name: 'Bitcoin Strategy Simulator',
      description: 'Backtest and simulate Bitcoin trading strategies',
      url: 'https://bitcoin-strategy-simulator.onrender.com/',
      color: '#f7931a',
      isExternal: true
    }
  ];

  const dataApps = [
    {
      id: 'ap-sync',
      name: 'AP Sync',
      description: 'Airtable-Pinecone synchronization',
      url: 'https://ap-sync.mangumstreet.com',
      color: '#06b6d4'
    }
  ];

  // Individual War Room components
  const extraApps = [
    {
      id: 'three3-health',
      name: '33 Health',
      description: 'Health reporting and analytics',
      url: 'https://three3healthreporting.onrender.com',
      color: '#16a34a',
      isExternal: true
    }
  ];

  const warRoomComponents = [
    {
      id: 'analytics',
      name: 'Analytics',
      description: 'Advanced market analytics and custom reports',
      url: 'https://analytics.mangumstreet.com',
      color: '#10b981'
    },
    {
      id: 'news',
      name: 'News & Sentiment',
      description: 'Real-time news and market sentiment analysis',
      url: 'https://news.mangumstreet.com',
      color: '#8b5cf6'
    },
    {
      id: 'micro',
      name: 'Microstructure',
      description: 'Deep market microstructure and order flow',
      url: 'https://micro.mangumstreet.com',
      color: '#f59e0b'
    },
    {
      id: 'market',
      name: 'Market Health',
      description: 'Real-time market health monitoring',
      url: 'https://market.mangumstreet.com',
      color: '#ef4444'
    },
    {
      id: 'position',
      name: 'Position & Risk',
      description: 'Position tracking and risk management',
      url: 'https://position.mangumstreet.com',
      color: '#f97316'
    }
  ];

  const handleAppClick = (app) => {
    if (app.isInternal) {
      if (app.id === 'cio-report') {
        setShowCIOReport(true);
      }
    } else {
      window.open(app.url, '_blank');
    }
  };

  const handleTradingAppClick = (url) => {
    if (!isTradingAuthenticated) {
      setPendingTradingUrl(url);
      setShowTradingAuthModal(true);
    } else {
      window.open(url, '_blank');
    }
  };

  const handleTradingAuthenticate = () => {
    setIsTradingAuthenticated(true);
    setShowTradingAuthModal(false);
    if (pendingTradingUrl) {
      window.open(pendingTradingUrl, '_blank');
      setPendingTradingUrl(null);
    }
  };

  const handleModalClose = () => {
    setShowTradingAuthModal(false);
    setPendingTradingUrl(null);
  };

  if (showCIOReport) {
    return <CIOReport onBack={() => setShowCIOReport(false)} />;
  }

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="brand-stack">
          <BrandLogo size={28} />
          <span className="brand-title">Mangum Street</span>
        </div>
        <div className="header-actions">
          {isTradingAuthenticated && (
            <span className="trading-status">🔓 Trading Access</span>
          )}
          <button onClick={onLogout} className="logout-button">Logout</button>
        </div>
      </header>

      <div className="dashboard-content">
        {/* Hero */}
        <motion.section
          className="hero-section"
          variants={fadeUp}
          initial={prefersReducedMotion ? 'visible' : 'hidden'}
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <div className="hero-bg" />
          <div className="hero-inner">
            <div className="hero-text">
              <h2 className="hero-title">Welcome to Mangum Street</h2>
              <p className="hero-subtitle">Unified access to analysis, trading, and data tools</p>
            </div>
          </div>
        </motion.section>

        {/* Primary Applications */}
        <motion.section
          className="section"
          variants={fadeUp}
          initial={prefersReducedMotion ? 'visible' : 'hidden'}
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <h2 className="section-title">Primary Applications</h2>
          <p className="section-description">Core platforms for trading and analysis</p>

          <div className="apps-primary-grid">
            {primaryAppsOrdered.map((app, idx) => (
              <motion.div
                key={app.id}
                className="app-tile"
                onClick={() => handleAppClick(app)}
                style={{ borderColor: app.color }}
                variants={slideInRight}
                initial={prefersReducedMotion ? 'visible' : 'hidden'}
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: prefersReducedMotion ? 0 : Math.min(idx * 0.04, 0.2) }}
              >
                <div className="app-header">
                  <div className="app-icon" style={{ backgroundColor: app.color }}>
                    {<AppGlyph id={app.id} /> || <span className="app-initials">{getAbbreviation(app.name)}</span>}
                  </div>
                  <div className="app-meta">
                    <h3 className="app-name">{app.name}</h3>
                    <span className="app-url">{app.url.replace('https://', '')}</span>
                  </div>
                </div>
                <p className="app-description">{app.description}</p>
                <div className="app-footer">
                  {app.isPublic && <span className="public-badge">PUBLIC</span>}
                  {app.isExternal && <span className="external-badge">EXTERNAL</span>}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Trading Tools */}
        <motion.section
          className="section"
          variants={fadeUp}
          initial={prefersReducedMotion ? 'visible' : 'hidden'}
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <h2 className="section-title">
            Trading Tools 
            {!isTradingAuthenticated && <span className="lock-icon">🔒</span>}
          </h2>
          <p className="section-description">
            Execution and strategy management
            {!isTradingAuthenticated && <span className="auth-notice"> (requires authentication)</span>}
          </p>
          
          <div className="apps-grid">
            {tradingApps.map((app, idx) => (
              <motion.div
                key={app.id}
                className={`app-tile ${!isTradingAuthenticated ? 'app-tile-locked' : ''}`}
                onClick={() => handleTradingAppClick(app.url)}
                style={{ borderColor: app.color }}
                variants={slideInRight}
                initial={prefersReducedMotion ? 'visible' : 'hidden'}
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: prefersReducedMotion ? 0 : Math.min(idx * 0.04, 0.2) }}
              >
                <div className="app-header">
                  <div className="app-icon" style={{ backgroundColor: app.color }}>
                    {<AppGlyph id={app.id} /> || <span className="app-initials">{getAbbreviation(app.name)}</span>}
                  </div>
                  <div className="app-meta">
                    <h3 className="app-name">{app.name}</h3>
                    <span className="app-url">{app.url.replace('https://', '')}</span>
                  </div>
                </div>
                <p className="app-description">{app.description}</p>
                <div className="app-footer">
                  {app.isExternal && <span className="external-badge">EXTERNAL</span>}
                </div>
                {!isTradingAuthenticated && (
                  <div className="lock-overlay">
                    <span className="lock-overlay-icon">🔒</span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Data Management */}
        <motion.section
          className="section"
          variants={fadeUp}
          initial={prefersReducedMotion ? 'visible' : 'hidden'}
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <h2 className="section-title">Data Management</h2>
          <p className="section-description">Data synchronization and management tools</p>
          
          <div className="apps-grid">
            {dataApps.map((app, idx) => (
              <motion.div
                key={app.id}
                className="app-tile"
                onClick={() => handleAppClick(app)}
                style={{ borderColor: app.color }}
                variants={slideInRight}
                initial={prefersReducedMotion ? 'visible' : 'hidden'}
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: prefersReducedMotion ? 0 : Math.min(idx * 0.04, 0.2) }}
              >
                <div className="app-header">
                  <div className="app-icon" style={{ backgroundColor: app.color }}>
                    {<AppGlyph id={app.id} /> || <span className="app-initials">{getAbbreviation(app.name)}</span>}
                  </div>
                  <div className="app-meta">
                    <h3 className="app-name">{app.name}</h3>
                    <span className="app-url">{app.url.replace('https://', '')}</span>
                  </div>
                </div>
                <p className="app-description">{app.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* War Room Components Info */}
        <motion.section
          className="info-section"
          variants={fadeUp}
          initial={prefersReducedMotion ? 'visible' : 'hidden'}
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <h2 className="section-title">War Room Components</h2>
          <p className="section-description">The War Room app includes these integrated tools (click to access directly):</p>
          <div className="component-list">
            {warRoomComponents.map((comp, idx) => (
              <motion.div 
                key={comp.id} 
                className="component-item clickable"
                onClick={() => handleAppClick(comp)}
                title={`Open ${comp.name}`}
                variants={slideInRight}
                initial={prefersReducedMotion ? 'visible' : 'hidden'}
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: prefersReducedMotion ? 0 : Math.min(idx * 0.03, 0.18) }}
              >
                <div className="component-icon-bg" style={{ backgroundColor: comp.color }}>
                  {<AppGlyph id={comp.id} /> || <span className="component-icon">{getAbbreviation(comp.name)}</span>}
                </div>
                <div className="component-details">
                  <span className="component-name">{comp.name}</span>
                  <span className="component-url">{comp.url.replace('https://', '')}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Extra Section */}
        <motion.section
          className="section"
          variants={fadeUp}
          initial={prefersReducedMotion ? 'visible' : 'hidden'}
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <h2 className="section-title">Extra</h2>
          <p className="section-description">Additional tools and resources</p>
          
          <div className="apps-grid">
            {extraApps.map((app, idx) => (
              <motion.div
                key={app.id}
                className="app-tile"
                onClick={() => handleAppClick(app)}
                style={{ borderColor: app.color }}
                variants={slideInRight}
                initial={prefersReducedMotion ? 'visible' : 'hidden'}
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: prefersReducedMotion ? 0 : Math.min(idx * 0.04, 0.2) }}
              >
                <div className="app-header">
                  <div className="app-icon" style={{ backgroundColor: app.color }}>
                    {<AppGlyph id={app.id} /> || <span className="app-initials">{getAbbreviation(app.name)}</span>}
                  </div>
                  <div className="app-meta">
                    <h3 className="app-name">{app.name}</h3>
                    <span className="app-url">{app.url.replace('https://', '')}</span>
                  </div>
                </div>
                <p className="app-description">{app.description}</p>
                <div className="app-footer">
                  {app.isExternal && <span className="external-badge">EXTERNAL</span>}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Quick Stats */}
        <motion.section
          className="stats-section"
          variants={fadeUp}
          initial={prefersReducedMotion ? 'visible' : 'hidden'}
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <div className="stat-card">
            <h4>Main Apps</h4>
            <p className="stat-value">16</p>
          </div>
          <div className="stat-card">
            <h4>War Room Tools</h4>
            <p className="stat-value">5</p>
          </div>
          <div className="stat-card">
            <h4>Status</h4>
            <p className="stat-value status-active">Active</p>
          </div>
        </motion.section>
      </div>

      {/* Trading Auth Modal */}
      <TradingAuthModal
        isOpen={showTradingAuthModal}
        onAuthenticate={handleTradingAuthenticate}
        onClose={handleModalClose}
      />
    </div>
  );
};

// Main App Component
function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const auth = localStorage.getItem(AUTH_CONFIG.session.storageKey);
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <div className="App">
      {isAuthenticated ? (
        <Dashboard onLogout={handleLogout} />
      ) : (
        <Login onLogin={handleLogin} />
      )}
    </div>
  );
}

export default App;
